import { validationMixin } from "vuelidate";
import {
  required,
  numeric,
  email,
  minValue,
  requiredIf
} from "vuelidate/lib/validators";

const MINIMUMORDERAMOUNT = 0;
const MINIMUMPERIOD = 1;
const MINIMUMSENDDATE = new Date();

const FREQUENCY_VALUES = [
  "Daily",
  "Weekly",
  "Fortnightly",
  "Monthly",
  "BiMonthly",
  "Quarterly",
  "Yearly"
];

const parseToFloat = value => parseFloat(value.toString().replace(",", ""));

const parseToInt = value => parseInt(value.toString().replace(",", ""));

const isFloat = value => !isNaN(parseToFloat(value));

const orderAmountNoLessThan = min => value => parseToFloat(value) > min;

const periodNoLessThan = min => value => parseToInt(value) > min;

const frequencyValid = value => FREQUENCY_VALUES.includes(value);

const checkDateOrder = (Before, After, AllowEqualDates) => {
  if (!(Before instanceof Date)) return false;
  if (!(After instanceof Date)) return false;

  if (!AllowEqualDates) return Before < After;

  return Before <= After;
};

const expiryDateBeforeSendDate = (value, vm) =>
  checkDateOrder(vm.sendDate, value);

const executionDateAtOrAfterExpiryDate = (value, vm) =>
  checkDateOrder(vm.expiryDate, value, true);

export default {
  parseToFloat,
  mixins: [validationMixin],
  validations: {
    form: {
      orderInformation: {
        firstName: {
          required
        },
        lastName: {
          required
        },
        email: {
          required,
          email
        },
        orderNumber: {
          required
        }
      },
      orderDetails: {
        amount: {
          required,
          isFloat,
          orderAmountNoLessThan: orderAmountNoLessThan(MINIMUMORDERAMOUNT)
        },
        currency: {
          required
        },
        initialPaymentAmount: {
          isFloat
        }
      },
      recurringInformation: {
        period: {
          required: requiredIf(function() {
            return !this.indefinitePeriod;
          }),
          numeric,
          periodNoLessThan: function(value, vm) {
            return (
              this.indefinitePeriod || periodNoLessThan(MINIMUMPERIOD)(value)
            );
          }
        },
        frequency: {
          required,
          frequencyValid
        },
        sendDate: {
          required,
          minValue: minValue(MINIMUMSENDDATE)
        },
        expiryDate: {
          required,
          expiryDateBeforeSendDate
        },
        executionDate: {
          required: requiredIf(function() {
            return this.executionDateEnabled;
          }),
          executionDateAtOrAfterExpiryDate: function(value, vm) {
            return (
              !this.executionDateEnabled ||
              executionDateAtOrAfterExpiryDate(value, vm)
            );
          }
        },
        reason: {
          required
        }
      }
    }
  },
  computed: {
    errorMessages() {
      return {
        orderInformation: this.orderInformationErrors,
        orderDetails: this.orderDetailsErrors,
        recurringInformation: this.recurringInformationErrors
      };
    },
    orderInformationErrors() {
      const parent = "orderInformation";

      return {
        firstName: this.generateErrorMessage(parent, "firstName"),
        lastName: this.generateErrorMessage(parent, "lastName"),
        email: this.generateErrorMessage(parent, "email"),
        orderNumber: this.generateErrorMessage(parent, "orderNumber")
      };
    },
    orderDetailsErrors() {
      const parent = "orderDetails";

      return {
        amount: this.generateErrorMessage(parent, "amount"),
        currency: this.generateErrorMessage(parent, "currency")
      };
    },
    recurringInformationErrors() {
      const parent = "recurringInformation";

      return {
        period: this.generateErrorMessage(parent, "period"),
        frequency: this.generateErrorMessage(parent, "frequency"),
        sendDate: this.generateErrorMessage(parent, "sendDate"),
        executionDate: this.generateErrorMessage(parent, "executionDate"),
        expiryDate: this.generateErrorMessage(parent, "expiryDate"),
        reason: this.generateErrorMessage(parent, "reason")
      };
    },
    validationMethods() {
      return [
        this.requiredMessage,
        this.numericMessage,
        this.orderAmountNoLessThanMessage,
        this.periodNoLessThanMessage,
        this.isFloatMessage,
        this.frequencyValidMessage,
        this.emailValidationMessage,
        this.minValueDateMessage,
        this.expiryDateBeforeSendDateMessage,
        this.executionDateAtOrAfterExpiryDateMessage
      ];
    }
  },
  methods: {
    generateErrorMessage(parent, name) {
      let validationInfo = this.$v.form[parent][name];
      if (!validationInfo.$dirty) return null;

      if (!validationInfo.$error) return "";

      let message = this.validationMethods.reduce(
        (errorMessage, validationMethod) =>
          errorMessage + validationMethod(validationInfo),
        ""
      );

      return message;
    },
    requiredMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("required")) return "";

      const message = "This is a required field. ";

      return validationInfo.required ? "" : message;
    },
    numericMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("numeric")) return "";

      const message = "This field only allows numeric values. ";

      return validationInfo.numeric ? "" : message;
    },
    orderAmountNoLessThanMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("orderAmountNoLessThan")) return "";

      const message = `The order amount must be greater than ${MINIMUMORDERAMOUNT}. `;

      return validationInfo.orderAmountNoLessThan ? "" : message;
    },
    periodNoLessThanMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("periodNoLessThan")) return "";

      const message = `The payment period must be greater than ${MINIMUMPERIOD}. `;

      return validationInfo.periodNoLessThan ? "" : message;
    },
    frequencyValidMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("frequencyValid")) return "";

      const message = `The payment frequency is not set to a valid value. `;

      return validationInfo.frequencyValid ? "" : message;
    },
    isFloatMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("isFloat")) return "";

      const message = "The amount entered is invalid. ";

      return validationInfo.isFloat ? "" : message;
    },
    emailValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("email")) return "";

      const message = "The email address entered is invalid. ";

      return validationInfo.email ? "" : message;
    },
    expiryDateBeforeSendDateMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("expiryDateBeforeSendDate")) return "";

      const message =
        "The expiry date must be at least one after the send date. ";

      return validationInfo.expiryDateBeforeSendDate ? "" : message;
    },
    executionDateAtOrAfterExpiryDateMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("executionDateAtOrAfterExpiryDate"))
        return "";

      const message =
        "The Date of First Payment must be the same day or after the expiry date. ";

      return validationInfo.executionDateAtOrAfterExpiryDate ? "" : message;
    },
    minValueDateMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("minValue")) return "";

      const message = "The send date cannot be a date in the past. ";

      return validationInfo.minValue ? "" : message;
    }
  }
};
